import translator from "@/translator";
import React, { useContext, useEffect, useState } from "react";
import { Sms, Whatsapp } from "@/note_office/icons/menu";
import { Switch, Input, Button, Form, message } from "antd";
import { StateContext } from "../State";
import { useNavigate } from "react-router-dom";
import BackButton from "@/note_office/shared/BackButton";
import useBulkDelivery from "../hooks/useBulkDelivery";
import Helper from "@/helper";

const t = translator("note_office.views.bulk_deliveries.new_view.");
const { TextArea } = Input;
const csrfToken = Helper.getCSRFToken();

const getSafeChannels = (sourceChannels, availableChannels = []) => {
  return {
    whatsapp: availableChannels.includes("whatsapp") ? !!sourceChannels.whatsapp : false,
    sms: availableChannels.includes("sms") ? !!sourceChannels.sms : false,
  };
};

const DeliveryView = () => {
  const { state, updateState } = useContext(StateContext);
  const {
    selectedTemplateText = "",
    selectedChannels = { whatsapp: true, sms: true },
    selectedContacts = [],
  } = state;

  const { create_url, create_template_url, available_channels } = state.bulkDeliveriesData || {};

  const [form] = Form.useForm();
  const [channels, setChannels] = useState(() =>
    getSafeChannels(selectedChannels, available_channels || []),
  );
  const [messageText, setMessageText] = useState(selectedTemplateText || "");

  const navigate = useNavigate();
  const { isLoading, sendBulkDelivery } = useBulkDelivery();

  const toggleChannel = (channel) => {
    if (!available_channels.includes(channel)) return;

    setChannels((prev) => {
      const updated = { ...prev, [channel]: !prev[channel] };

      if (!updated.whatsapp && !updated.sms) {
        updated[channel === "sms" ? "whatsapp" : "sms"] = true;
      }

      const enabledChannels = available_channels.filter((ch) => updated[ch]);

      if (enabledChannels.length === 0) {
        return prev;
      }

      return updated;
    });
  };

  useEffect(() => {
    updateState({ selectedChannels: channels });
  }, [channels]);

  useEffect(() => {
    if (available_channels) {
      setChannels((prev) => getSafeChannels(prev, available_channels));
    }
  }, [available_channels]);

  const handleMessageChange = (e) => {
    const text = e.target.value;
    setMessageText(text);
    updateState({ selectedTemplateText: text });
  };

  const handleSendBulkDelivery = async () => {
    form.setFields([]);

    try {
      await sendBulkDelivery({
        create_url,
        text: messageText,
        channels,
        chategy_customer_client_ids: selectedContacts,
        onSuccess: () => {
          form.resetFields();
          navigate("/send_delivery");
        },
        onError: (errors) => {
          if (typeof errors === "string") {
            message.error(errors);
            return;
          }

          if (errors.text) {
            form.setFields([{ name: "text", errors: errors.text }]);
          }

          Object.entries(errors).forEach(([_key, value]) => {
            message.error(value);
          });
        },
      });
    } catch {
      message.error(t("errors.server_error"));
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const response = await fetch(create_template_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          template: {
            text: messageText,
            channels: channels,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t("template_created"));
      } else if (data.errors?.text) {
        form.setFields([{ name: "text", errors: data.errors.text }]);
      } else {
        message.error(t("errors.server_error"));
      }
    } catch {
      message.error(t("errors.server_error"));
    }
  };

  const handleGoToTemplates = () => navigate("/template");
  const handleBackToContacts = () => navigate("/");

  useEffect(() => {
    if (selectedTemplateText && selectedTemplateText !== form.getFieldValue("text")) {
      form.setFieldsValue({ text: selectedTemplateText });
    }
  }, [selectedTemplateText]);

  return (
    <div className="wrapper wrapper__menu-layout overflow">
      <BackButton onClick={handleBackToContacts} text={t("title")} />

      <Form
        form={form}
        layout="vertical"
        className="bulk_deliveries-new__content overflow"
        onFinish={handleSendBulkDelivery}
      >
        <div>
          <h3 className="bulk_deliveries-new__label">{t("channels_label")}</h3>
          <p className="bulk_deliveries-new__description">{t("channels_description")}</p>

          {available_channels.map((channel) => (
            <div className="profile__menu profile__menu-margin" key={channel}>
              <div className="profile__menu-item bulk_deliveries-new__menu-item">
                {channel === "whatsapp" ? <Whatsapp /> : <Sms />}
                <span className="bulk_deliveries-new__channels">{t(channel)}</span>
                <span className="ant-menu-item-icon">
                  <Switch
                    checked={channels[channel]}
                    onChange={() => toggleChannel(channel)}
                    disabled={!available_channels.includes(channel)}
                  />
                </span>
              </div>
            </div>
          ))}

          <Form.Item name="text" className="bulk_deliveries-new__text">
            <TextArea
              value={messageText}
              onChange={handleMessageChange}
              placeholder={t("message_placeholder")}
              rows={5}
              className="bulk_deliveries-new__textarea"
            />
          </Form.Item>

          <div className="bulk_deliveries__actions">
            <a
              className="bulk_deliveries__actions_button bulk_deliveries-new__btn-action"
              onClick={handleCreateTemplate}
            >
              {t("create_template")}
            </a>

            <a
              className="bulk_deliveries__actions_button bulk_deliveries-new__btn-action"
              onClick={handleGoToTemplates}
            >
              {t("my_templates")}
            </a>
          </div>
        </div>

        <div className="button-group">
          <Button
            type="primary"
            className="button-base"
            htmlType="submit"
            block
            loading={isLoading}
            disabled={isLoading || selectedContacts.length === 0}
          >
            {t("send_to_contacts", { count: selectedContacts.length })}
          </Button>

          <Button
            type="primary"
            className="button-base button-blue"
            block
            onClick={() => {
              navigate("/schedule");
            }}
            disabled={isLoading || selectedContacts.length === 0}
          >
            {t("schedule")}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DeliveryView;
